import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Upload, X, Image as ImageIcon, Palette, FileText, AlertCircle, CheckCircle, Copy, ExternalLink, Globe, Link2, Link2Off, List, ListOrdered } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';

interface BlogSettings {
  logo_url: string | null;
  about_text: string;
  accent_color: string;
  blog_page_url: string | null;
}

interface BlogSettingsSectionProps {
  userId: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export interface BlogSettingsHandle {
  saveBlogSettings: () => Promise<boolean>;
}

export const BlogSettingsSection = forwardRef<BlogSettingsHandle, BlogSettingsSectionProps>(
  ({ userId, onSuccess, onError }, ref) => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState<BlogSettings>({
    logo_url: null,
    about_text: '',
    accent_color: '#FF6B35',
    blog_page_url: null
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Underline,
    ],
    content: settings.about_text,
    onUpdate: ({ editor }) => {
      setSettings(prev => ({ ...prev, about_text: editor.getHTML() }));
    },
  }, []);

  useEffect(() => {
    loadBlogSettings();
  }, [userId]);

  useEffect(() => {
    if (editor && settings.about_text !== editor.getHTML()) {
      editor.commands.setContent(settings.about_text);
    }
  }, [settings.about_text, editor]);

  const loadBlogSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_blog_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          logo_url: data.logo_url,
          about_text: data.about_text || '',
          accent_color: data.accent_color || '#FF6B35',
          blog_page_url: data.blog_page_url || null
        });
      }
    } catch (err) {
      console.error('Error loading blog settings:', err);
      onError?.('Kon blog instellingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      onError?.('Upload alleen PNG, JPG, SVG of WEBP bestanden');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      onError?.('Logo mag maximaal 2MB zijn');
      return;
    }

    try {
      setUploading(true);

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        throw new Error('Niet ingelogd');
      }

      let fileToUpload: File | Blob = file;
      let fileExt = file.name.split('.').pop();

      if (file.type !== 'image/svg+xml') {
        const compressionResult = await compressImage(file, 800, 2, 'webp', 0.85);
        fileToUpload = new File(
          [compressionResult.blob],
          file.name.replace(/\.[^.]+$/, '.webp'),
          { type: 'image/webp' }
        );
        fileExt = 'webp';
      }

      const fileName = `${authUser.id}/logo-${Date.now()}.${fileExt}`;

      if (settings.logo_url) {
        const oldPath = settings.logo_url.split('/').slice(-2).join('/');
        await supabase.storage.from('blog-logos').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('blog-logos')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-logos')
        .getPublicUrl(fileName);

      setSettings(prev => ({ ...prev, logo_url: publicUrl }));
      onSuccess?.('Logo succesvol geupload');
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      onError?.(err.message || 'Kon logo niet uploaden');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!settings.logo_url) return;

    try {
      const oldPath = settings.logo_url.split('/').slice(-2).join('/');
      await supabase.storage.from('blog-logos').remove([oldPath]);
      setSettings(prev => ({ ...prev, logo_url: null }));
      onSuccess?.('Logo verwijderd');
    } catch (err: any) {
      console.error('Error removing logo:', err);
      onError?.(err.message || 'Kon logo niet verwijderen');
    }
  };

  const handleCopyUrl = async () => {
    if (!settings.blog_page_url) return;

    try {
      await navigator.clipboard.writeText(settings.blog_page_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      onError?.('Kon URL niet kopiëren');
    }
  };


  const saveBlogSettings = async (): Promise<boolean> => {
    try {
      console.log('Saving blog settings:', { userId, settings });

      const { error } = await supabase
        .from('user_blog_settings')
        .upsert({
          user_id: userId,
          logo_url: settings.logo_url,
          about_text: settings.about_text,
          accent_color: settings.accent_color,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Blog settings saved successfully');
      return true;
    } catch (err: any) {
      console.error('Error saving blog settings:', err);
      throw err;
    }
  };

  useImperativeHandle(ref, () => ({
    saveBlogSettings
  }));

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <h2 className="text-lg font-bold text-[#231F20] mb-2 flex items-center gap-2">
        <ImageIcon className="w-5 h-5" />
        Blog Voorkeuren
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Personaliseer hoe je blog widget eruit ziet. Deze instellingen worden gebruikt in de widget code generator hieronder.
      </p>

      <div className="space-y-6">
        {settings.blog_page_url && (
          <div className="bg-gradient-to-r from-[#FF6B35]/5 to-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#231F20] mb-1">
                  Jouw Blog Pagina URL
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  Deel deze URL om mensen naar je blog overzichtspagina te leiden
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border border-gray-300 rounded-lg px-4 py-2.5 min-w-0">
                    <p className="text-sm text-[#FF6B35] font-mono truncate">
                      {settings.blog_page_url}
                    </p>
                  </div>
                  <button
                    onClick={handleCopyUrl}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                    title="Kopieer URL"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">Gekopieerd!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">Kopieer</span>
                      </>
                    )}
                  </button>
                  <a
                    href={settings.blog_page_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#FF6B35] text-white rounded-lg hover:bg-[#FF6B35]/90 transition-colors flex-shrink-0"
                    title="Open blog pagina"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">Open</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Upload je bedrijfslogo. Het logo wordt automatisch geschaald (max 300px breed, 120px hoog op overzicht). Ondersteund: PNG, JPG, SVG of WEBP (max 2MB).
          </p>

          {settings.logo_url ? (
            <div className="relative inline-block">
              <img
                src={settings.logo_url}
                alt="Logo preview"
                className="w-48 h-auto max-h-32 object-contain border border-gray-200 rounded-lg p-2 bg-white"
              />
              <button
                onClick={handleRemoveLogo}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#FF6B35] transition-colors bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {uploading ? 'Uploaden...' : 'Klik om logo te uploaden'}
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                disabled={uploading}
              />
            </label>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Over Ons Tekst
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Introduceer jezelf of je bedrijf. Deze tekst verschijnt bovenaan je blog overzichtspagina.
          </p>

          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1 flex-wrap">
              <button
                onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${editor?.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
                type="button"
                title="Heading 1"
              >
                H1
              </button>
              <button
                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${editor?.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
                type="button"
                title="Heading 2"
              >
                H2
              </button>
              <button
                onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                className={`px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold ${editor?.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''}`}
                type="button"
                title="Heading 3"
              >
                H3
              </button>
              <div className="w-px bg-gray-300 mx-1" />
              <button
                onClick={() => editor?.chain().focus().toggleBold().run()}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('bold') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('italic') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Italic"
              >
                <em>I</em>
              </button>
              <button
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('underline') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Underline"
              >
                <u>U</u>
              </button>
              <div className="w-px bg-gray-300 mx-1" />
              <button
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('bulletList') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('orderedList') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Numbered List"
              >
                <ListOrdered className="w-4 h-4" />
              </button>
              <div className="w-px bg-gray-300 mx-1" />
              <button
                onClick={() => {
                  const url = window.prompt('Voer URL in:');
                  if (url) {
                    editor?.chain().focus().setLink({ href: url }).run();
                  }
                }}
                className={`p-2 rounded hover:bg-gray-200 ${editor?.isActive('link') ? 'bg-gray-300' : ''}`}
                type="button"
                title="Link toevoegen"
              >
                <Link2 className="w-4 h-4" />
              </button>
              {editor?.isActive('link') && (
                <button
                  onClick={() => editor?.chain().focus().unsetLink().run()}
                  className="p-2 rounded hover:bg-gray-200 text-red-600"
                  type="button"
                  title="Link verwijderen"
                >
                  <Link2Off className="w-4 h-4" />
                </button>
              )}
            </div>
            <EditorContent
              editor={editor}
              className="prose max-w-none p-4 min-h-[120px] focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Accentkleur
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Deze kleur wordt gebruikt voor links, quotes en buttons op je blog pagina en in email campaigns
          </p>

          <div className="flex items-center gap-4">
            <input
              type="color"
              value={settings.accent_color}
              onChange={(e) => setSettings(prev => ({ ...prev, accent_color: e.target.value }))}
              className="w-20 h-12 rounded-lg border border-gray-300 cursor-pointer"
            />
            <div className="flex-1">
              <input
                type="text"
                value={settings.accent_color}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                    setSettings(prev => ({ ...prev, accent_color: value }));
                  }
                }}
                placeholder="#FF6B35"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none font-mono"
              />
            </div>
          </div>

          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-xs font-medium text-gray-700 mb-3">Preview:</p>
            <div className="space-y-2">
              <a
                href="#"
                style={{ color: settings.accent_color }}
                className="block hover:underline"
                onClick={(e) => e.preventDefault()}
              >
                Voorbeeld link met je accentkleur
              </a>
              <button
                style={{ backgroundColor: settings.accent_color }}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                type="button"
              >
                Voorbeeld button
              </button>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Let op:</strong> Deze instellingen zijn alleen van toepassing op je publieke blog pagina.
            Je kunt per email campaign de kleuren en styling nog aanpassen.
          </p>
        </div>
      </div>
    </div>
  );
});

BlogSettingsSection.displayName = 'BlogSettingsSection';
